// Mock Timestamp for shared package (avoid Firebase dependency)
export interface TimestampLike {
  seconds: number;
  nanoseconds?: number;
  toDate(): Date;
}

/**
 * Represents various types of date inputs that can be processed by the date utilities.
 */
export type DateInput =
  | string
  | Date
  | TimestampLike
  | { seconds: number }
  | null
  | undefined;

/**
 * Represents the format options for date formatting functions.
 * Can be a predefined format string or custom Intl.DateTimeFormatOptions.
 */
export type DateFormatType =
  | "short"
  | "medium"
  | "long"
  | "full"
  | Intl.DateTimeFormatOptions;

/**
 * Converts any supported date format to a JavaScript Date object.
 */
export const toDate = (date: DateInput): Date | null => {
  if (!date) return null;

  try {
    if (date instanceof Date) return date;
    if (date && typeof date === "object" && "toDate" in date) return date.toDate();
    if (typeof date === "string") {
      const parsedDate = new Date(date);
      return isNaN(parsedDate.getTime()) ? null : parsedDate;
    }
    if (typeof date === "object" && "seconds" in date) {
      return new Date(date.seconds * 1000);
    }
  } catch (error) {
    console.error("Error converting date:", error);
  }

  return null;
};

/**
 * Resolves a format type to Intl.DateTimeFormatOptions.
 */
export const resolveFormatOptions = (
  format: DateFormatType,
): Intl.DateTimeFormatOptions => {
  if (typeof format === "string") {
    switch (format) {
      case "short":
        return { month: "numeric", day: "numeric", year: "2-digit" };
      case "medium":
        return { month: "short", day: "numeric", year: "numeric" };
      case "long":
        return { month: "long", day: "numeric", year: "numeric" };
      case "full":
        return {
          weekday: "long",
          month: "long",
          day: "numeric",
          year: "numeric",
        };
      default:
        return { month: "short", day: "numeric", year: "numeric" }; // Default to medium
    }
  }
  return format;
};

/**
 * Formats a single date according to the specified format.
 */
export const formatDate = (
  dateInput: DateInput,
  format: DateFormatType = "medium",
): string => {
  const date = toDate(dateInput);
  if (!date) return "";

  try {
    const options = resolveFormatOptions(format);
    return new Intl.DateTimeFormat("en-US", options).format(date);
  } catch (error) {
    console.error("Error formatting date:", error);
    return "";
  }
};

/**
 * Formats a date range.
 */
export const formatDateRange = (
  startDateInput: DateInput,
  endDateInput: DateInput,
  format: DateFormatType = "medium",
): string => {
  const startDate = toDate(startDateInput);
  if (!startDate) return "";

  const endDate = toDate(endDateInput);
  if (!endDate) return formatDate(startDate, format);

  // Same day case
  if (startDate.getTime() === endDate.getTime()) {
    return formatDate(startDate, format);
  }

  // For same month and year, we can use a special format
  if (
    startDate.getMonth() === endDate.getMonth() &&
    startDate.getFullYear() === endDate.getFullYear()
  ) {
    // Format just the month
    const baseOptions = resolveFormatOptions(format);
    const monthOptions: Intl.DateTimeFormatOptions = {
      month:
        typeof format === "string"
          ? format === "short"
            ? "numeric"
            : format === "medium"
              ? "short"
              : "long"
          : baseOptions.month || "long",
    };

    const monthFormatter = new Intl.DateTimeFormat("en-US", monthOptions);
    const month = monthFormatter.format(startDate);

    // Get the days and year
    return `${month} ${startDate.getDate()}-${endDate.getDate()}, ${startDate.getFullYear()}`;
  }

  // Different months or years - use full formatting for both dates
  return `${formatDate(startDate, format)} - ${formatDate(endDate, format)}`;
};

/**
 * Formats a 24-hour time string (HH:mm) to localized 12-hour format
 * @param timeString - Time in HH:mm format (e.g., "18:00")
 * @returns Formatted time (e.g., "6:00 PM")
 */
export const formatTime = (
  timeString: string | undefined | null,
): string => {
  if (!timeString || !timeString.trim()) {
    return "";
  }

  const [hours, minutes] = timeString.split(":").map(Number);

  if (
    isNaN(hours) ||
    isNaN(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return timeString; // Return original if invalid
  }

  const period = hours >= 12 ? "PM" : "AM";
  const displayHour = hours % 12 || 12;
  const displayMinutes = minutes.toString().padStart(2, "0");

  return `${displayHour}:${displayMinutes} ${period}`;
};

/**
 * Formats time as a string, either from explicit time strings or from a date object.
 */
export const formatTimeRange = (
  startTime?: string,
  endTime?: string,
  startDate?: DateInput,
  includeTime = false,
): string => {
  try {
    if (startTime) {
      const formattedStart = formatTime(startTime);
      if (endTime) {
        const formattedEnd = formatTime(endTime);
        return `${formattedStart} - ${formattedEnd}`;
      }
      return formattedStart;
    }

    if (includeTime && startDate) {
      const date = toDate(startDate);
      if (!date || isNaN(date.getTime())) return "";

      try {
        return date.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
      } catch (error) {
        console.error("Error formatting time:", error);
        return "";
      }
    }

    return "";
  } catch (error) {
    console.error("Unexpected error in formatTimeRange:", error);
    return "";
  }
};

/**
 * Checks if a date is in the past
 */
export const isPastDate = (dateInput: DateInput): boolean => {
  const date = toDate(dateInput);
  return date ? date < new Date() : false;
};

/**
 * Calculates days between two dates
 */
export const getDaysDifference = (
  startDateInput: DateInput,
  endDateInput: DateInput = new Date(),
): number => {
  const startDate = toDate(startDateInput);
  const endDate = toDate(endDateInput);

  if (!startDate || !endDate) return 0;

  // Reset time part to compare days only
  const start = new Date(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate(),
  );
  const end = new Date(
    endDate.getFullYear(),
    endDate.getMonth(),
    endDate.getDate(),
  );

  const diffTime = Math.abs(end.getTime() - start.getTime());
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};
